const defaultFetchConfigs = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/json` },
};

const addToCartReducerFactory = ({ DEFAULT_ACTIONS, publish }) =>
    /**
     * See https://shopify.dev/api/ajax/reference/cart#post-locale-cart-add-js
     * @param {any} cart An object that contains `items` which contains the list of products to add.
     * @returns A promise that resolves to an object containing the updates of the cart
     */
    async ({ items }) => {
        const payload = { ...defaultFetchConfigs };
        payload.body = JSON.stringify({ items });

        const { ADD_TO_CART, ADDING_TO_CART, ADD_TO_CART_ERROR } = DEFAULT_ACTIONS;

        try {
            publish({ type: ADDING_TO_CART, cart: { items } });
            const response = await fetch(`${routes.cart_add_url}`, payload);
            const cartUpdates = await response.json();
            return { type: ADD_TO_CART, cartUpdates };
        } catch (error) {
            publish({
                type: ADD_TO_CART_ERROR,
                source: "addToCart Reducer",
                error
            });

            throw error;
        }
    }

const changeCartReducerFactory = ({ DEFAULT_ACTIONS, publish }) =>
    /**
     * https://shopify.dev/api/ajax/reference/cart#post-locale-cart-change-js
     * @param {any} cartUpdate - The changes you need to happen, you may use either `line` or `id`. see https://shopify.dev/api/ajax/reference/cart#identify-the-line-item-to-change
     * @returns A promise that resolves to an object containing the updates of the cart
     */
    async (cartUpdate) => {
        const payload = { ...defaultFetchConfigs };
        payload.body = JSON.stringify(cartUpdate);

        const { CHANGE_CART, CHANGING_CART, CHANGE_CART_ERROR } = DEFAULT_ACTIONS;

        try {
            publish({ type: CHANGING_CART, cartUpdate });
            const response = await fetch(`${routes.cart_change_url}`, payload);
            const cartUpdates = await response.json();
            return { type: CHANGE_CART, cartUpdates };
        } catch (error) {
            publish({
                type: CHANGE_CART_ERROR,
                source: "changeCart Reducer",
                error
            });

            throw error;
        }
    }

const removeFromCartReducerFactory = (SMCart) =>
    /**
     * Uses changeCartReducerFactory as a verbose way to remove cart line items. 
     * Basically calls change chart and sets the quantity to 0.
     * https://shopify.dev/api/ajax/reference/cart#post-locale-cart-change-js
     * @param {string} key - The line item key that looks like 44421662114095:ef0a087ac399582ff43a0b927d63fc6f. See https://shopify.dev/api/liquid/objects/line_item#line_item-key
     * @returns A promise that resolves to an object containing the updates of the cart
     */
    async ({ key, id }) => {
        const changeCart = changeCartReducerFactory(SMCart);
        return await changeCart({
            id: key ? key : id,
            quantity: 0
        });
    }

const onGenericError = () => (action) => {
    const { source, error } = action;
    console.error(`An error occured when executing ${source}`);
    console.error(error);
    //  Do more things to the error if necessary
    return action;
}

const assertRootVariablesPlaced = () => {
    if (!routes) {
        throw new Error("Will not be able to use shopify cart related functionality due to missing `routes` in the theme scripts.");
    }

    if (!routes.cart_add_url || !routes.cart_change_url) {
        throw new Error("The urls `cart_add_url` and `cart_change_url` are required in routes.");
    }
}

(function (root, factory) {
    if (typeof define === 'function' && define.amd) {
        define(['exports'], factory);
    } else if (typeof exports === 'object' && typeof exports.nodeName !== 'string') {
        factory(exports);
    } else {
        factory((root.SMCart = {}));
    }
}(typeof self !== 'undefined' ? self : this, function (exports) {
    const subscribers = {};
    const reducers = {};

    exports.subscribe = (type, callback) => {
        if (subscribers[type] === undefined) {
            subscribers[type] = []
        }

        subscribers[type] = [...subscribers[type], callback];

        return function unsubscribe() {
            subscribers[type] = subscribers[type].filter((cb) => {
                return cb !== callback
            });
        }
    };
    exports.on = exports.subscribe;

    exports.dispatch = async (action) => {
        const { type } = action;
        let reducerOutput = { ...action };
        if (reducers[type]) {
            reducerOutput = await reducers[type].reduce(async (_, reducer) => {
                return await reducer(action);
            }, Promise.resolve());
        }

        const output = { ...action, ...reducerOutput };
        exports.publish(type, output);
        return output;
    }

    exports.publish = (type, data) => {
        if (subscribers[type]) {
            subscribers[type].forEach((callback) => {
                callback(data)
            })
        }
    }

    /**
     * 
     * @param {string} type The type of event this reducer gets called on
     * @param {function} reducerFactory Pass in a higher order function `const addToCart = (SMCart) => (action) => {...}
     * @returns 
     */
    exports.registerReducer = (type, reducerFactory) => {
        if (reducers[type] === undefined) {
            reducers[type] = []
        }

        reducers[type] = [...reducers[type], reducerFactory(exports)];

        return function removeReducer() {
            reducers[type] = reducers[type].filter((cb) => {
                return cb !== callback
            });
        }
    }

    exports.DEFAULT_ACTIONS = {
        ADD_TO_CART: "addToCart",
        ADD_TO_CART_ERROR: "addToCart.error",
        ADDING_TO_CART: "addToCart.inProgress",
        CHANGE_CART: "changeCart",
        CHANGE_CART_ERROR: "changeCart.error",
        CHANGING_CART: "changeCart.inProgress",
        REMOVE_FROM_CART: "removeFromCart",
    }

    assertRootVariablesPlaced();

    const { ADD_TO_CART, CHANGE_CART, REMOVE_FROM_CART, ADD_TO_CART_ERROR, CHANGE_CART_ERROR } = exports.DEFAULT_ACTIONS;
    exports.registerReducer(ADD_TO_CART_ERROR, onGenericError);
    exports.registerReducer(CHANGE_CART_ERROR, onGenericError);

    exports.registerReducer(ADD_TO_CART, addToCartReducerFactory);
    exports.registerReducer(CHANGE_CART, changeCartReducerFactory);
    exports.registerReducer(REMOVE_FROM_CART, removeFromCartReducerFactory);

    //  Also allow for direct access in case Mike needs it
    //  Ideally though, actions should just be dispatched
    exports.addToCart = addToCartReducerFactory(exports);
    exports.changeCart = changeCartReducerFactory(exports);
    exports.removeFromCart = removeFromCartReducerFactory(exports);

}));